/**
 * Code Extractor Utility
 * 
 * Extracts code blocks from AI responses and prepares them for Monaco editor updates
 */

/**
 * Remove inline UI structured content blocks from text
 * These are JSON blocks wrapped in [STRUCTURED:type]...[/STRUCTURED] markers
 * that should not appear in code diffs
 * @param {string} text - Text that may contain structured blocks
 * @returns {string} Text with structured blocks removed
 */
export function stripStructuredBlocks(text) {
  if (!text) return text;
  
  // Remove [STRUCTURED:type]...JSON...[/STRUCTURED] blocks
  // Handle both complete and incomplete blocks
  const structuredPattern = /\[STRUCTURED:[a-z-]+\]\s*[\s\S]*?\s*\[\/STRUCTURED*\]/gi;
  const incompletePattern = /\[STRUCTURED:[a-z-]+\][\s\S]*$/i;
  
  let cleanText = text.replace(structuredPattern, '');
  cleanText = cleanText.replace(incompletePattern, '');
  
  return cleanText.trim();
}

/**
 * Extract all code blocks from markdown text
 * @param {string} text - Markdown text containing code blocks
 * @returns {Array} Array of {language, code} objects
 */
export function extractCodeBlocks(text) {
  if (!text) return [];
  
  // First strip any inline UI structured content blocks
  const cleanText = stripStructuredBlocks(text);
  
  // Match code blocks with optional language specifier
  const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
  const matches = [...cleanText.matchAll(codeBlockRegex)];
  
  return matches.map(match => ({
    language: match[1] || 'text',
    code: stripStructuredBlocks(match[2].trim()) // Also clean the code content itself
  }));
}

/**
 * Extract the first code block from text
 * @param {string} text - Markdown text
 * @returns {string|null} Extracted code or null
 */
export function extractFirstCodeBlock(text) {
  const blocks = extractCodeBlocks(text);
  return blocks.length > 0 ? blocks[0].code : null;
}

/**
 * Check if AI response contains code that should update the editor
 * @param {string} text - AI response text
 * @param {string} currentFilePath - Currently open file path
 * @returns {boolean}
 */
export function shouldUpdateEditor(text, currentFilePath) {
  if (!text || !currentFilePath) return false;
  
  // Check for code blocks
  const hasCodeBlocks = /```[\s\S]+```/.test(text);
  if (!hasCodeBlocks) return false;
  
  // Check for question indicators - if it's just a question, don't update
  const questionIndicators = [
    /^(?:what|how|why|when|where|can you explain)/i,
    /\bexplain\b/i,
    /\bshow me\b/i,
    /\btell me\b/i,
    /\bwhat (?:does|is|are)\b/i,
    /\bhow (?:does|is|can)\b/i,
    /\?\s*$/m  // Ends with a question mark
  ];
  
  const hasQuestionIndicators = questionIndicators.some(pattern => pattern.test(text));
  
  // Check for indicators that AI is providing updated code
  const updateIndicators = [
    /here(?:'s| is) (?:the )?(?:updated|modified|corrected|fixed)/i,
    /i(?:'ve| have) (?:updated|modified|changed|fixed|added|refactored)/i,
    /(?:let me|i'll|i will) (?:update|modify|change|fix|add|refactor)/i,
    /replace.*with/i,
    /new (?:code|version|implementation)/i,
    /complete (?:code|file|implementation)/i,
    /(?:updated|modified|changed|fixed) (?:code|file|implementation)/i,
    /apply (?:this|these) change/i,
    /make (?:this|these) change/i
  ];
  
  const hasUpdateIndicators = updateIndicators.some(pattern => pattern.test(text));
  
  // If there are question indicators and no clear update indicators, don't update
  if (hasQuestionIndicators && !hasUpdateIndicators) {
    return false;
  }
  
  // Only update if there are clear update indicators
  return hasUpdateIndicators;
}

/**
 * Detect if text contains inline code changes (not full file replacement)
 * @param {string} text - AI response text
 * @returns {boolean}
 */
export function containsInlineChanges(text) {
  const inlineChangePatterns = [
    /change line \d+/i,
    /replace line \d+/i,
    /add (?:this|these) line/i,
    /insert (?:at|after) line \d+/i,
    /on line \d+/i
  ];
  
  return inlineChangePatterns.some(pattern => pattern.test(text));
}

/**
 * Extract replace operations from AI response (oldString -> newString)
 * @param {string} text - AI response text
 * @returns {Object|null} Replace operation or null
 */
export function extractReplaceOperation(text) {
  // Pattern 1: Replace X with Y format
  const replaceWithPattern = /replace[\s\S]*?```[\w]*\n?([\s\S]*?)```[\s\S]*?with[\s\S]*?```[\w]*\n?([\s\S]*?)```/i;
  const replaceMatch = text.match(replaceWithPattern);
  
  if (replaceMatch) {
    return {
      type: 'replace',
      oldString: replaceMatch[1].trim(),
      newString: replaceMatch[2].trim()
    };
  }
  
  // Pattern 2: Change X to Y format
  const changeToPattern = /change[\s\S]*?```[\w]*\n?([\s\S]*?)```[\s\S]*?to[\s\S]*?```[\w]*\n?([\s\S]*?)```/i;
  const changeMatch = text.match(changeToPattern);
  
  if (changeMatch) {
    return {
      type: 'replace',
      oldString: changeMatch[1].trim(),
      newString: changeMatch[2].trim()
    };
  }
  
  return null;
}

/**
 * Detect if text contains unified diff format
 * @param {string} text - AI response text
 * @returns {boolean}
 */
export function containsUnifiedDiff(text) {
  // Look for unified diff markers
  const unifiedDiffPattern = /^@@\s*-\d+(?:,\d+)?\s+\+\d+(?:,\d+)?\s+@@/m;
  const diffHeaderPattern = /^---\s+a\//m;
  
  return unifiedDiffPattern.test(text) || diffHeaderPattern.test(text);
}

/**
 * Extract unified diff content from AI response
 * @param {string} text - AI response text
 * @returns {string|null} Unified diff content or null
 */
export function extractUnifiedDiff(text) {
  if (!containsUnifiedDiff(text)) {
    return null;
  }
  
  // Extract diff from code block (with 'diff' language marker)
  const diffBlockRegex = /```(?:diff)?\n?((?:---|\+\+\+|@@)[\s\S]*?)```/;
  const match = text.match(diffBlockRegex);
  
  if (match) {
    return match[1].trim();
  }
  
  // Try to extract diff without code block markers
  const lines = text.split('\n');
  const diffStartIndex = lines.findIndex(line => 
    line.startsWith('---') && (line.includes('a/') || line.includes('/dev/null'))
  );
  
  if (diffStartIndex === -1) {
    return null;
  }
  
  // Extract from diff start to end of diff
  const diffLines = [];
  let inDiffHunk = false;
  
  for (let i = diffStartIndex; i < lines.length; i++) {
    const line = lines[i];
    
    // Start of diff headers
    if (line.startsWith('---')) {
      diffLines.push(line);
      inDiffHunk = true;
      continue;
    }
    
    if (line.startsWith('+++')) {
      diffLines.push(line);
      continue;
    }
    
    // Diff hunk header
    if (line.match(/^@@\s*-\d+/)) {
      diffLines.push(line);
      inDiffHunk = true;
      continue;
    }
    
    // Inside a diff hunk - check for valid diff line prefixes
    if (inDiffHunk) {
      // Valid diff line: starts with space, +, -, or \ (no newline marker)
      if (line.match(/^[ +\-\\]/)) {
        diffLines.push(line);
        continue;
      }
      
      // Empty line might be part of diff context
      if (line === '') {
        diffLines.push(line);
        continue;
      }
      
      // Stop conditions: markdown, code block, or invalid content
      if (line.startsWith('#') || 
          line.startsWith('```') || 
          (line.match(/^[a-zA-Z]/) && !line.match(/^[ +\-\\]/))) {
        // Stop - we've reached the end of the diff
        break;
      }
    }
  }
  
  return diffLines.length > 0 ? diffLines.join('\n') : null;
}

/**
 * Extract code and metadata for editor update
 * @param {string} text - AI response text
 * @param {string} currentFilePath - Currently open file path
 * @returns {Object|null} Update object or null
 */
export function extractCodeUpdate(text, currentFilePath) {
  if (!shouldUpdateEditor(text, currentFilePath)) {
    return null;
  }
  
  // First, check if this is a unified diff (preferred for Monaco diff view)
  const unifiedDiff = extractUnifiedDiff(text);
  if (unifiedDiff) {
    return {
      filePath: currentFilePath,
      updateType: 'diff',
      diffContent: unifiedDiff,
      isPartialUpdate: true,
      isDiff: true,
      originalResponse: text
    };
  }
  
  // Second, check if this is a replace operation (partial modification)
  const replaceOp = extractReplaceOperation(text);
  if (replaceOp) {
    return {
      filePath: currentFilePath,
      updateType: 'replace',
      oldString: replaceOp.oldString,
      newString: replaceOp.newString,
      isPartialUpdate: true,
      originalResponse: text
    };
  }
  
  // Otherwise, extract full file content
  const codeBlocks = extractCodeBlocks(text);
  if (codeBlocks.length === 0) {
    return null;
  }
  
  // Use the first substantial code block (skip very small snippets)
  const substantialBlock = codeBlocks.find(block => block.code.length > 20);
  if (!substantialBlock) {
    return null;
  }
  
  return {
    filePath: currentFilePath,
    updateType: 'full',
    content: substantialBlock.code,
    language: substantialBlock.language,
    isInlineChange: containsInlineChanges(text),
    isPartialUpdate: false,
    originalResponse: text
  };
}

const CodeExtractor = {
  stripStructuredBlocks,
  extractCodeBlocks,
  extractFirstCodeBlock,
  shouldUpdateEditor,
  containsInlineChanges,
  extractReplaceOperation,
  extractCodeUpdate,
  containsUnifiedDiff,
  extractUnifiedDiff
};

export default CodeExtractor;
